import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useToast } from "@/components/ui/use-toast"

type Message = {
  role: 'user' | 'assistant'
  content: string
}

interface ChatProps {
  initialPrompt?: string;
}

export const Chat = ({ initialPrompt = '' }: ChatProps) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState(initialPrompt)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (initialPrompt) {
      sendMessage(undefined, initialPrompt)
    }
  }, []) // Run once when component mounts

  const sendMessage = async (e?: React.FormEvent, content?: string) => {
    if (e) e.preventDefault()
    
    const messageContent = content || input
    if (!messageContent.trim()) return

    const newMessage: Message = { role: 'user', content: messageContent }
    const newMessages = [...messages, newMessage]
    setMessages(newMessages)
    setInput('')
    setIsLoading(true)

    try {
      console.log('Sending messages to Claude:', newMessages)
      const { data, error } = await supabase.functions.invoke('chat', {
        body: { messages: newMessages }
      })

      if (error) {
        console.error('Supabase function error:', error)
        throw error
      }

      if (!data?.content?.[0]?.text) {
        console.error('Invalid response format:', data)
        throw new Error('Invalid response from Claude')
      }

      console.log('Claude response:', data)
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: data.content[0].text 
      }])
    } catch (error) {
      console.error('Error:', error)
      toast({
        title: "Error",
        description: "Failed to get response from Claude. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-4 space-y-4">
      <ScrollArea className="h-[500px] p-4 border rounded-lg">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`mb-4 p-4 rounded-lg ${
              message.role === 'user' 
                ? 'bg-primary text-white ml-auto max-w-[80%]' 
                : 'bg-gray-100 mr-auto max-w-[80%]'
            }`}
          >
            {message.content}
          </div>
        ))}
        {isLoading && (
          <div className="text-center text-gray-500">
            Claude is thinking...
          </div>
        )}
      </ScrollArea>

      <form onSubmit={sendMessage} className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          disabled={isLoading}
          className="flex-1"
        />
        <Button type="submit" disabled={isLoading}>
          Send
        </Button>
      </form>
    </div>
  )
}